import { Component, EventEmitter, Output } from '@angular/core';

import { PortalApiService } from '../../portal-api.service';
import { PortalPreviewService } from '../../portal-preview.service';
import { CurrencyPipe } from '../currency';
import { portalDate } from '../portal-format';
import { PortalModalBaseComponent } from '../portal-modal/portal-modal-base.component';

// "View Change Order Details" modal. Extends the shared portal-modal shell (just
// like the reject / to-be-confirmed modals). open(order) loads the line-level
// changes for that change order. The accept / reject / to-be-confirmed / comment
// actions are emitted back to the list, which owns the reason modals + tab counts.
@Component({
	selector: 'portal-change-order-details-modal',
	templateUrl: './change-order-details-modal.component.html'
})
export class ChangeOrderDetailsModalComponent extends PortalModalBaseComponent {

	@Output() comment = new EventEmitter<any>();
	@Output() confirm = new EventEmitter<any>();
	@Output() reject = new EventEmitter<any>();
	@Output() accept = new EventEmitter<any>();
	// The embedded document viewer is full-screen and lives on the list, so these
	// bubble up rather than opening a modal inside a modal.
	@Output() viewProposal = new EventEmitter<any>();
	@Output() viewChangeOrder = new EventEmitter<any>();

	order: any = null;
	loading = false;
	error = '';
	detail: any = null;

	constructor(private api: PortalApiService, public preview: PortalPreviewService) {
		super();
	}

	// Revision name (if any) before the revision number, e.g. "CO 1.1".
	get revLabel(): string {
		if (!this.order) return '';
		return (this.order.revision_name ? this.order.revision_name + ' ' : '') + (this.order.revision_label ?? '');
	}

	get modalTitle(): string {
		return this.order ? `Change Order ${this.revLabel}` : '';
	}

	get subtitle(): string {
		if (!this.order) return '';
		return `Quote #${this.order.quote_no}` + (this.order.title ? ` · ${this.order.title}` : '');
	}

	// Groups that actually have line items (hide empty sections).
	get detailGroups(): any[] {
		return (this.detail?.groups || []).filter((g: any) => g.lines && g.lines.length);
	}

	// Only the itemised breakdown needs the extra width; without it the modal is just
	// the timeline and the totals, which read better narrow.
	get modalSize(): 'lg' | 'xl' {
		return this.order?.show_co_breakdown ? 'xl' : 'lg';
	}

	// Columns the group band spans before its quantity / total cells — Item plus
	// whichever of System / Area the staff turned on.
	get itemColspan(): number {
		return 3 + (this.detail?.show_system ? 1 : 0) + (this.detail?.show_area ? 1 : 0);
	}

	// Quantities: up to 4 decimals, trailing zeros dropped, matching the staff
	// table's `decimal:0:4`. "Changed" rows are deltas, so they carry a sign.
	qty(value: number, signed = false): string {
		const n = Number(value) || 0;
		const text = String(Math.round(n * 10000) / 10000);
		return signed && n > 0 ? '+' + text : text;
	}

	// A "changed" band holds the difference between the old and new line, so its
	// figures read as +/- deltas; added and removed bands are absolute.
	private isDelta(group: any): boolean {
		return group?.kind === 'change';
	}

	lineQty(line: any, group: any): string {
		return this.qty(line?.quantity, this.isDelta(group));
	}

	lineTotal(line: any, group: any): string {
		return this.isDelta(group)
			? this.signedMoney(line?.total, this.detail?.currency)
			: CurrencyPipe.transform(line?.total, this.detail?.currency || this.order?.currency_id);
	}

	groupQty(group: any): string {
		return this.qty(group?.quantity, this.isDelta(group));
	}

	groupTotal(group: any): string {
		return this.isDelta(group)
			? this.signedMoney(group?.total, this.detail?.currency)
			: CurrencyPipe.transform(group?.total, this.detail?.currency || this.order?.currency_id);
	}

	open(order: any): void {
		this.order = order;
		this.detail = null;
		this.error = '';
		this.loading = true;
		this.api.project.changeOrderChanges(order.id, (res: any) => {
			this.detail = res || null;
			this.loading = false;
			this.modal.open();
		}, (err: any) => {
			this.loading = false;
			this.error = err?.message || 'Could not load change details.';
		});
	}

	// Null-safe: the footer is bound to portal-modal's [footer] input and can be
	// evaluated before/after an order is set.
	isPending(): boolean {
		return this.order?.status === 'pending_review' || this.order?.status === 'reviewed' || this.order?.status === 'to_be_confirmed';
	}

	canConfirm(): boolean {
		return this.order?.status === 'pending_review' || this.order?.status === 'reviewed';
	}

	// Signed money for the change value (+/- prefix) — the sign sits outside the
	// currency affix, so it can't go through the pipe on its own.
	signedMoney(value: number, currency?: string): string {
		const v = value || 0;
		return (v > 0 ? '+' : v < 0 ? '-' : '') + CurrencyPipe.transform(Math.abs(v), currency || this.order?.currency_id);
	}

	badgeClass(status: string): string {
		switch (status) {
			case 'accepted': return 'badge-success';          // green
			case 'declined': return 'badge-danger';           // purple
			case 'to_be_confirmed': return 'badge-secondary'; // grey
			default: return 'badge-warning';                  // pending_review / reviewed
		}
	}

	badgeLabel(status: string): string {
		switch (status) {
			case 'accepted': return 'Accepted';
			case 'declined': return 'Declined';
			case 'to_be_confirmed': return 'To Be Confirmed';
			case 'reviewed': return 'Reviewed';
			default: return 'Pending Review';
		}
	}

	dateLabel(): { text: string; cls: string } {
		const o = this.order;
		if (!o) return { text: '', cls: '' };
		if (o.status === 'accepted') {
			return { text: o.accepted_date ? `Accepted on ${portalDate(o.accepted_date)}` : 'Accepted', cls: 'qr-date-accepted' };
		}
		if (o.status === 'declined') {
			return { text: '', cls: 'qr-date-overdue' };
		}
		if (o.expiry_date) {
			const overdue = new Date(o.expiry_date) < new Date();
			return overdue
				? { text: `Overdue ${portalDate(o.expiry_date)}`, cls: 'qr-date-overdue' }
				: { text: `Valid until ${portalDate(o.expiry_date)}`, cls: 'qr-date-pending' };
		}
		return { text: '', cls: 'qr-date-pending' };
	}

	// Open the change-order web page (full detail + accept) in a new tab. Bubbles up
	// so the list opens it — the list owns reloading itself when the customer returns.
	openDetailsPage(): void {
		if (this.order?.can_view_change_order) this.viewChangeOrder.emit(this.order);
	}

	// The same, for the change order's published proposal.
	openProposal(): void {
		if (this.order?.can_view_proposal) this.viewProposal.emit(this.order);
	}

	// The reason modals + tab counts live on the list, so bubble the actions up.
	onComment(): void { this.comment.emit(this.order); }
	onConfirm(): void { this.confirm.emit(this.order); }
	onReject(): void { this.reject.emit(this.order); }
	onAccept(): void { this.accept.emit(this.order); }
}
